import { useMemo, useState, useCallback } from "react";
import type { RecordFilters } from "@/lib/query-keys";

export interface RecordsFilterInitialState {
  hasPin?: boolean | null;
  selectedPlanId?: string | null;
}

export function useRecordsFilterState(
  initial: RecordsFilterInitialState = {},
) {
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showUntagged, setShowUntagged] = useState(false);
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>(
    initial.selectedPlanId ? [initial.selectedPlanId] : [],
  );
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [selectedCreatorIds, setSelectedCreatorIds] = useState<string[]>([]);
  const [hasPin, setHasPin] = useState<boolean | null>(initial.hasPin ?? null);

  const toggleTag = useCallback((tagId: string) => {
    setShowUntagged(false);
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
    );
  }, []);

  const toggleUntagged = useCallback(() => {
    setShowUntagged((prev) => {
      const next = !prev;
      if (next) setSelectedTags([]);
      return next;
    });
  }, []);

  const toggleCreator = useCallback((creatorId: string) => {
    setSelectedCreatorIds((prev) =>
      prev.includes(creatorId)
        ? prev.filter((id) => id !== creatorId)
        : [...prev, creatorId],
    );
  }, []);

  const clearHasPin = useCallback(() => setHasPin(null), []);

  const togglePlan = useCallback((planId: string) => {
    setSelectedPlanIds((prev) =>
      prev.includes(planId)
        ? prev.filter((id) => id !== planId)
        : [...prev, planId],
    );
  }, []);

  const clearAll = useCallback(() => {
    setSearchQuery(null);
    setSelectedTags([]);
    setShowUntagged(false);
    setSelectedPlanIds([]);
    setDateFrom(null);
    setDateTo(null);
    setSelectedCreatorIds([]);
    setHasPin(null);
  }, []);

  const filters: RecordFilters = useMemo(
    () => ({
      search: searchQuery,
      tags: selectedTags.length > 0 ? selectedTags : undefined,
      hasTags: showUntagged ? false : null,
      plan: selectedPlanIds.length > 0 ? selectedPlanIds : undefined,
      date_from: dateFrom,
      date_to: dateTo,
      created_by:
        selectedCreatorIds.length > 0 ? selectedCreatorIds : undefined,
      has_pin: hasPin,
    }),
    [
      searchQuery,
      selectedTags,
      showUntagged,
      selectedPlanIds,
      dateFrom,
      dateTo,
      selectedCreatorIds,
      hasPin,
    ],
  );

  return {
    searchQuery,
    setSearchQuery,
    selectedTags,
    toggleTag,
    showUntagged,
    toggleUntagged,
    selectedPlanIds,
    togglePlan,
    setSelectedPlanIds,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    selectedCreatorIds,
    toggleCreator,
    hasPin,
    setHasPin,
    clearHasPin,
    clearAll,
    filters,
  };
}
